import React, { useState, useEffect, useRef, useMemo } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import { FaPen, FaPlus, FaSearch, FaThumbsUp, FaFilter, FaTimes } from 'react-icons/fa';
import { ImCancelCircle } from 'react-icons/im';
import api from '../../services/api';
import Alert from '../../components/Alert';
import AlertaSedes from '../../components/AlertaSedes';
import { FinField, FinInput } from '../../components/FinField';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../../css/financeiro-parcela-pagar.css';

export default function PainelFornecedorGrupo() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    // Mesma lista do back (FornecedorGrupoController::PERFIS_EXCLUSAO) e do Excluir de fornecedor
    const podeExcluir = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro', 'suplier'].includes(perfil);
    // `fornecedor` acha os grupos de um fornecedor pelo nome, nome fantasia ou CNPJ de um membro.
    const [filtro, setFiltro] = useState({ nome: '', fornecedor: '' });
    // Mais filtros: o modal edita um rascunho, que só vale no "Aplicar filtros".
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const [rascunhoFornecedor, setRascunhoFornecedor] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [fornecedorGrupos, setFornecedorGrupos] = useState([]);

    // Paginação e seleção
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const controllerRef = useRef(null);

    const toggleSelect = id => setSelectedIds(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    // Compara contra A PÁGINA, não contra o total selecionado (mesma correção do painel de
    // fornecedores): com 15 marcados na página 1, o cabeçalho da página 2 aparecia marcado e
    // clicar nele apagava a seleção inteira. Agora a seleção alimenta a exclusão.
    const paginaToda = () => paginados.length > 0 && paginados.every(r => selectedIds.has(r.id));
    const toggleAll = () => setSelectedIds(prev => {
        const n = new Set(prev);
        const marcarTudo = !paginaToda();

        paginados.forEach(r => (marcarTudo ? n.add(r.id) : n.delete(r.id)));
        return n;
    });

    // Ordem feita aqui: a lista de grupos vem inteira do servidor. Abre pelos grupos com mais
    // fornecedores; o 1º clique numa coluna ordena crescente (Fornecedores, decrescente), o seguinte inverte.
    const [ordem, setOrdem] = useState({ campo: 'fornecedores', dir: 'desc' });
    const ordenarPor = (campo) => {
        setOrdem(atual => (atual.campo === campo
            ? { campo, dir: atual.dir === 'asc' ? 'desc' : 'asc' }
            : { campo, dir: campo === 'fornecedores' ? 'desc' : 'asc' }));
        setPage(1);
    };
    const seta = (campo) => (ordem.campo === campo ? (ordem.dir === 'asc' ? ' ▲' : ' ▼') : '');

    const nomesSedes = (grupo) => (grupo.sedes ?? []).map(sede => sede.nomefantasia || sede.nome).join(', ');

    const ordenados = useMemo(() => {
        const valor = (grupo) => {
            if (ordem.campo === 'fornecedores') return Number(grupo.fornecedores_count ?? 0);
            if (ordem.campo === 'codigo') return Number(grupo.codigo ?? 0);
            if (ordem.campo === 'sede') return nomesSedes(grupo);
            return grupo.nome ?? '';
        };
        return [...fornecedorGrupos].sort((a, b) => {
            const va = valor(a);
            const vb = valor(b);
            const comparacao = typeof va === 'number' ? va - vb : va.localeCompare(vb, 'pt-BR');
            // empate desfeito pelo nome, sempre em ordem alfabética
            return (ordem.dir === 'asc' ? comparacao : -comparacao)
                || (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR');
        });
        // eslint-disable-next-line
    }, [fornecedorGrupos, ordem.campo, ordem.dir]);

    const total = fornecedorGrupos.length;
    const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
    const paginados = ordenados.slice((page - 1) * pageSize, page * pageSize);

    // Exclusão encolhe a lista: a página em que se estava pode deixar de existir.
    useEffect(() => {
        if (page > totalPaginas) setPage(totalPaginas);
    }, [page, totalPaginas]);

    // Exclui os grupos e os vínculos deles com fornecedores; os fornecedores continuam cadastrados.
    const excluirSelecionados = () => {
        const ids = [...selectedIds];

        Alert({
            icon: "error",
            confirmButton: "Sair",
            denyButton: <><FaThumbsUp size={20} /> Excluir</>,
            title: "Excluir",
            html: (ids.length === 1 ? 'O grupo selecionado será excluído.' : ids.length + ' grupos serão excluídos.')
                + ' Os fornecedores continuam cadastrados; só deixam de fazer parte do grupo.',
            callBack: (result) => {
                if (result.isDenied) {
                    api({ method: 'delete', action: '/fornecedorGrupos', token: token, data: { idGrupos: ids } })
                        .then(() => {
                            toast.success(ids.length === 1 ? 'Grupo excluído.' : ids.length + ' grupos excluídos.');
                            setSelectedIds(new Set());
                            buscar();
                        })
                        .catch(() => toast.error('Não foi possível excluir. Tente novamente.'));
                }
            }
        });
    };

    // Recebe o filtro para quem acabou de mudá-lo: o `setFiltro` ainda não valeu neste render.
    const buscar = (filtroUsado = filtro) => {
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setBuscando(true);
        api({
            method: 'get',
            action: '/fornecedorGrupo',
            token: token,
            // As sedes vêm junto para a coluna Sede e o aviso de mais de uma sede na rede; a
            // contagem, para a coluna Fornecedores.
            params: { ...filtroUsado, incluirSedes: true, incluirContagem: true },
            signal: controllerRef.current.signal
        })
            .then(response => {
                setFornecedorGrupos(response.data.data);
                setBuscando(false);
            }).catch(err => {
                if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
                //console.log(err)
                setBuscando(false);
            });
    };

    useEffect(() => {
        document.querySelector('#nome').focus();
        buscar();
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
    },
        // eslint-disable-next-line
        []);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div><h1 className="fin-pp__title">Grupos de Fornecedor</h1></div>
                    <div className="fin-pp__actions">
                        <Link to="/cadastroFornecedorGrupo" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                            <FaPlus size={14} /> Novo Grupo
                        </Link>
                    </div>
                </div>

                <form
                    className="fin-pp__card fin-pp__search"
                    style={{ marginBottom: 16, padding: '12px 16px' }}
                    onSubmit={e => { buscar(); e.preventDefault(); }}
                >
                    <div className="fin-pp__search-field">
                        <FaSearch className="fin-pp__search-ic" size={16} />
                        <input
                            id="nome"
                            className="fin-pp__input"
                            type="text"
                            placeholder="Buscar grupo..."
                            onChange={e => setFiltro({ ...filtro, nome: e.target.value })}
                        />
                    </div>
                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={buscando}>
                        {buscando ? 'Buscando...' : 'Buscar'}
                    </button>
                    <button
                        type="button"
                        className="fin-pp__btn"
                        onClick={() => { setRascunhoFornecedor(filtro.fornecedor); setMostrarFiltros(true); }}
                    >
                        <FaFilter size={14} /> Mais filtros{filtro.fornecedor ? ' (1)' : ''}
                    </button>
                </form>

                {filtro.fornecedor && (
                    <div style={{ marginBottom: 12 }}>
                        <span className="fin-pp__pill is-info">
                            Grupos com o fornecedor: {filtro.fornecedor}
                            <button
                                type="button"
                                className="fin-pp__rowbtn"
                                style={{ marginLeft: 6, width: 18, height: 18 }}
                                title="Tirar este filtro"
                                onClick={() => { const novo = { ...filtro, fornecedor: '' }; setFiltro(novo); buscar(novo); }}
                            >
                                <FaTimes size={10} />
                            </button>
                        </span>
                    </div>
                )}

                {mostrarFiltros && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) setMostrarFiltros(false); }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaFilter size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Filtros avançados
                                        <span className="fin-pp__modal-sub">Refine a lista de grupos</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={() => setMostrarFiltros(false)}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="fin-pp__filters">
                                        <FinField label="Fornecedor" full>
                                            <FinInput
                                                placeholder="Nome, nome fantasia ou CNPJ de um membro"
                                                value={rascunhoFornecedor}
                                                onChange={e => setRascunhoFornecedor(e.target.value)}
                                            />
                                        </FinField>
                                    </div>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={() => setRascunhoFornecedor('')}>
                                        Limpar filtros
                                    </button>
                                    <button
                                        type="button"
                                        className="fin-pp__btn fin-pp__btn--primary"
                                        onClick={() => {
                                            const novo = { ...filtro, fornecedor: rascunhoFornecedor.trim() };
                                            setFiltro(novo);
                                            setMostrarFiltros(false);
                                            buscar(novo);
                                        }}
                                    >
                                        <FaSearch size={14} /> Aplicar filtros
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {podeExcluir && selectedIds.size > 0 && (
                    <div className="fin-pp__bulkbar" style={{ marginBottom: 8 }}>
                        <span className="fin-pp__bulkbar-count">{selectedIds.size} selecionado(s)</span>
                        <div className="fin-pp__bulkbar-actions">
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                onClick={excluirSelecionados}
                            >
                                <ImCancelCircle size={14} /> Excluir
                            </button>
                        </div>
                    </div>
                )}

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input
                                            type="checkbox"
                                            checked={paginaToda()}
                                            onChange={toggleAll}
                                        />
                                    </th>
                                    <th style={{ width: 60 }}></th>
                                    <th className="is-sortable" onClick={() => ordenarPor('codigo')}>Código{seta('codigo')}</th>
                                    <th className="is-sortable" onClick={() => ordenarPor('nome')}>Nome{seta('nome')}</th>
                                    <th className="is-sortable" onClick={() => ordenarPor('sede')}>Sede{seta('sede')}</th>
                                    <th className="is-sortable text-end" onClick={() => ordenarPor('fornecedores')}>Fornecedores{seta('fornecedores')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buscando
                                    ? <tr><td colSpan={6} className="fin-pp__empty"><span className="fin-pp__loading"><span className="fin-pp__spin"></span>Buscando...</span></td></tr>
                                    : paginados.length === 0
                                        ? <tr><td colSpan={6} className="fin-pp__empty">Nenhum registro</td></tr>
                                        : paginados.map(row => (
                                            <tr key={row.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.has(row.id)}
                                                        onChange={() => toggleSelect(row.id)}
                                                    />
                                                </td>
                                                <td>
                                                    <Link to={'/cadastroFornecedorGrupo/' + row.id} className="fin-pp__rowbtn">
                                                        <FaPen size={14} />
                                                    </Link>
                                                </td>
                                                <td className="fin-pp__cell-muted">{row.codigo ?? ''}</td>
                                                <td className="fin-pp__cell-strong">
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                        {row.nome ?? ''}
                                                        <AlertaSedes sedes={row.sedes} grupoId={row.id} />
                                                    </span>
                                                </td>
                                                <td>{nomesSedes(row)}</td>
                                                <td className={'fin-pp__cell-num' + (row.fornecedores_count ? '' : ' is-zero')}>
                                                    {row.fornecedores_count ?? 0}
                                                </td>
                                            </tr>
                                        ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{total} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage(p => p + 1)}>Próxima</button>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
